import React, { useEffect, useMemo, useState } from "react";
import { ICollection } from "../../types/ICollection";
import { MapSelectionSummary } from "./MapSelectionSummary";
import { classNameMapper } from "../../utils/classNameMapper";
import { createPortal } from "react-dom";
import { Modal } from "../../components/Modal";
import { useGetMapActionPreflightQuery, useGetMapActionsQuery, useInvokeMapActionMutation } from "../../slices/apiSlice";
import { IMapAction } from "../../types/IMapAction";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Field, Formik } from "formik";
import { Button } from "../../design/Button";
const snippetFilteredImage = require('../../../app/assets/snippet-selection-filtered.png');
const snippetNotFilteredImage = require('../../../app/assets/snippet-selection-not-filtered.png');
const optionImages: {[key: string]: any} = {
  'handle-overlap.allow': require('../../../app/assets/snippet-selection-overlap-allow.png'),
  'handle-overlap.deselect': require('../../../app/assets/snippet-selection-overlap-deselect.png'),
  'handle-overlap.replace': require('../../../app/assets/snippet-selection-overlap-replace.png'),
};

interface IDefaultActions {
  clear: () => void;
  goTo: () => void;
  filterBy: () => void;
  combine: () => void;
  downloadReport: ((filter: boolean) => Promise<string>) | undefined;
  canDownloadReport: () => boolean;
}

export type SelectedFeatures = {[key: string]: {[key: string]: any}};
type SelectionIDs = {[key: string]: string[]};

interface IMapSelectionActionsProps {
  mapID: number;
  selectedFeatures: SelectedFeatures;
  defaultActions: IDefaultActions;
  collections: ICollection[];
  reload?: () => void;
}

export type ActionPreflightRequest = {
  actionID: number;
  selection: SelectionIDs;
}

export type ActionPreflightResponse = {
  required: {
    fields: {name: string, column: string, type: string}[];
    decisions: Decision[];
    confirmationMessage: string | undefined;
  };
  messages: string[];
  errors: string[];
}

type Decision = {
  id: string;
  description: string;
  useImages: boolean;
  options: DecisionOption[];
}

type DecisionOption = {
  id: string;
  text: string;
  confirmationMessage: string | undefined;
}

type Answers = {[key: string]: string | number};

type Input = {
  fields?: Answers
  decisions?: Answers
};

export type ActionInvokeRequest = {
  actionID: number;
  selection: {[key: string]: any[]};
  input: Input;
}

export type ActionInvokeResponse = {
  completed: boolean;
  isError: boolean;
  message: string;
}

export const MapSelectionActions: React.FC<IMapSelectionActionsProps & JSX.IntrinsicAttributes> = ({ mapID, selectedFeatures, defaultActions, collections, reload }: IMapSelectionActionsProps) => {

  const [showSummary, setShowSummary] = useState(true);
  const [errorMsg, setErrorMsg] = useState('');
  const [downloading, setDownloading] = useState(false);
  const [askForReportSettings, setAskForReportSettings] = useState(false);
  const [applyFilters, setApplyFilters] = useState(true);
  const [backendActionInProgress, setBackendActionInProgress] = useState<IMapAction>();
  const [confirmationMessages, setConfirmationMessages] = useState<string[]>();
  const [confirmed, setConfirmed] = useState(false);

  const { data: backendActions = [] } = useGetMapActionsQuery(mapID);
  const selectedCollections = useMemo(() => Object.keys(selectedFeatures).map(layerName => collections?.find(c => c.tableName == layerName)), [selectedFeatures, collections]);
  const enabledBackendActions = useMemo(() => {
    return backendActions.filter(a => a.enabled && (a.sourceID === null || !!selectedCollections.find(c => c?.ID === a.sourceID)))
  }, [backendActions, selectedCollections]);
  const selectionIDs = useMemo(() => Object.keys(selectedFeatures).reduce((accum, layerName) => ({...accum, [layerName]: Object.keys(selectedFeatures[layerName])}), {}), [selectedFeatures]);
  const ableToSummarize = collections.filter(c => !!c.aggSql).length > 0;

  // Make the preflight call if an action is in progress on a non-empty selection.
  const buildPreflightRequest = () =>
    backendActionInProgress && backendActionInProgress.ID && {actionID: backendActionInProgress.ID, selection: selectionIDs};
  const { data: preflightResponseRaw, isFetching: preflightInProgress } = useGetMapActionPreflightQuery(buildPreflightRequest() || skipToken);
  // Never use a cached preflight while another one is in-progress.
  const preflightResponse = preflightResponseRaw && !preflightInProgress ? preflightResponseRaw : undefined;

  // Handle error coming from preflight.
  if (!errorMsg && preflightResponse?.errors.length) {
    setErrorMsg(preflightResponse.errors[0]);
    setBackendActionInProgress(undefined);
  }

  // If preflight requires a top-level confirmation, show the message to the user.
  if (!confirmationMessages && preflightResponse?.required.confirmationMessage) {
    setConfirmationMessages([preflightResponse.required.confirmationMessage]);
  }

  // If preflight does not require any fields, set submitted fields to an empty answer
  // so that we know to proceed with invoking the action.
  const [submittedFields, setSubmittedFields] = useState<Answers>();
  if (!submittedFields && preflightResponse?.required.fields.length === 0) {
    setSubmittedFields({});
  }

  // If preflight does not require any decisions, set submitted decisions to an empty answer
  // so that we know to proceed with invoking the action.
  const [submittedDecisions, setSubmittedDecisions] = useState<Answers>();
  if (!submittedDecisions && preflightResponse?.required.decisions.length === 0) {
    setSubmittedDecisions({});
  }

  // If preflight does not require decisions and does not require top-level confirmation, mark this as confirmed.
  if (!confirmed && !preflightResponse?.required.confirmationMessage && preflightResponse?.required.decisions.length === 0) {
    setConfirmed(true);
  }

  // If fields and decisions are both set, and we've received confirmation from the user, complete the input for the invocation.
  const [backendActionInput, setBackendActionInput] = useState<Input>();
  if (!backendActionInput && submittedFields && submittedDecisions && confirmed) {
    setBackendActionInput({ fields: submittedFields, decisions: submittedDecisions });
  }

  // If input is set and there's no error from preflight, invoke the action.
  const [invokeBackendAction, invokeResponse ] = useInvokeMapActionMutation();
  useEffect(() => {
    (async () => {
      if (backendActionInProgress?.ID && preflightResponse && !preflightResponse?.errors.length) {
        if (backendActionInput != undefined) {
          try {
            await invokeBackendAction({actionID: backendActionInProgress.ID, selection: selectionIDs, input: backendActionInput || {}}).unwrap();
            defaultActions.clear();
            if (backendActionInProgress.reloadOnCompletion && reload) {
              reload();
            }
          } catch(e) {
            console.log("Error invoking action", e); // The global error handler will already display an error modal.
          } finally {
            setBackendActionInProgress(undefined);
            setBackendActionInput(undefined);
            setConfirmationMessages(undefined);
            setConfirmed(false);
          }
        }
      }
    })();
  }, [backendActionInProgress, preflightResponse, backendActionInput]);

  function toggleSummary() {
    setShowSummary(!showSummary);
  };

  function clickToDownload() {
    if (downloading || !defaultActions || !defaultActions.downloadReport) {
      return;
    }

    setDownloading(true);
    setAskForReportSettings(true);
  }

  async function downloadReport() {
    setAskForReportSettings(false);

    if (defaultActions && defaultActions.downloadReport) {
      setErrorMsg(await defaultActions.downloadReport(applyFilters))
    }

    setDownloading(false);
  }

  function cancelDownload() {
    setAskForReportSettings(false);
    setDownloading(false);
  }

  function oxfordConcat(vals: string[], addQuotes: boolean = false): string {
    if (addQuotes) {
      vals = vals.map(s => `"${s}"`);
    }

    if (vals.length == 0) {
      return "";
    }
    if (vals.length == 1) {
      return vals[0];
    }
    if (vals.length == 2) {
      return vals.join("and");
    }

    // Prefix last element with "and", then join all with commas.
    vals[vals.length-1] = "and " + vals[vals.length-1];
    return vals.join(", ");
  }

  function onSelectFilterOption(e: any) {
    setApplyFilters(e.target.value === "true")
  }

  /**
   * Begin working to perform an action at the user's request.
   * 1. Make a preflight call to the backend to check if any user input is required.
   * 2. If input required, open a modal to get it (the modal will then invoke the action
   *    or cancel). If no input required, invoke the action right away.
   */
  async function startBackendAction(action: IMapAction) {
    setBackendActionInProgress(action);
  }

  function cancelAction() {
    setBackendActionInProgress(undefined);
    setBackendActionInput(undefined);
    setConfirmationMessages([]);
    setConfirmed(false);
  }

  // A single confirmation will clear the first of any required confirmations.
  function confirm() {
    setConfirmationMessages(confirmationMessages?.slice(1));
    setConfirmed(!confirmationMessages?.slice(1).length);
  }


  // Translate the layer names into propert Collection names.
  const selectedCollectionNames = selectedCollections.map(c => c?.name || "");

  return <>
  <ul className="actions">
    <li><a className="go-to" onClick={defaultActions.goTo}>Go To</a></li>
    <li><a className="filter-by" onClick={defaultActions.filterBy}>Filter By</a></li>
    { ableToSummarize && 
    <li><a className="summarize" onClick={toggleSummary}>{showSummary ? "Hide" : "Show"} Summary</a></li>
    }
    { defaultActions.downloadReport && defaultActions.canDownloadReport() &&
    <li><a className={`download-report ${classNameMapper({loading: downloading})}`} onClick={clickToDownload}>{downloading ? 'Downloading' : 'Download Report'}</a></li>
    }
    { enabledBackendActions.map(action => (
    <li key={action.ID}><a onClick={startBackendAction.bind(null, action)} className={`${classNameMapper({loading: backendActionInProgress?.ID == action.ID && (preflightInProgress || invokeResponse.isLoading)})}`}>{action.label || action.defaultName}</a></li>
    ))}
    <li><a className="clear" onClick={defaultActions.clear}>Clear Selection</a></li>
  </ul>
  { ableToSummarize && showSummary && createPortal(
    <MapSelectionSummary selectedFeatures={selectedFeatures} collections={collections}/>,
    document.getElementById("map-holder")!)
  }

  <Modal isOpen={askForReportSettings} onClose={() => {}} id="truterritory-selection-report-modal">
    <div className="modal-header">
      <h4>Download Report</h4>
    </div>
    <div className="modal-body">
      <p>You have selected shapes in {oxfordConcat(selectedCollectionNames, true)}. How would you like to display the shapes in {oxfordConcat(selectedCollectionNames, true)} which you didn't select?</p>
      <div className="image-buttons" onChange={onSelectFilterOption}>
        <label>
          <img src={snippetFilteredImage} alt="selection" />
          <input type="radio" name="report-filter-option" value="true" checked={applyFilters} />
          <span>Filter out other shapes in selected layers</span>
        </label>
        <label>
          <img src={snippetNotFilteredImage} alt="selection" />
          <input type="radio" name="report-filter-option" value="false" />
          <span>Highlight my selection, but show all shapes</span>
        </label>
      </div>
      <div className="controls">
        <button className="btn light" type="button" onClick={downloadReport}>
          Download Report
        </button>
        <button className="btn plain" type="button" onClick={cancelDownload}>Cancel</button>
      </div>
      <div className="modal-footer"></div>
    </div>
  </Modal>

  <Modal isOpen={!!errorMsg} onClose={() => {}} id="truterritory-selection-error-modal">
    <div className="modal-body">
      <h4>Error</h4>
      <p>Could not perform action. {errorMsg}</p>
      <div className="controls">
        <button className="btn orange" type="button" onClick={setErrorMsg.bind(null, '')}>
          Close
        </button>
      </div>
      <div className="modal-footer"></div>
    </div>
  </Modal>

  <Modal isOpen={!!confirmationMessages && confirmationMessages.length > 0} onClose={() => {}} id="truterritory-selection-error-modal">
    <div className="modal-body">
      <h4>Confirmation Required</h4>
      <p>{confirmationMessages && confirmationMessages[0]}</p>
      <div className="controls">
        <button className="btn light" type="button" onClick={confirm}>
          Yes, continue
        </button>
        <button className="btn plain" type="button" onClick={cancelAction}>Cancel</button>
      </div>
      <div className="modal-footer"></div>
    </div>
  </Modal>

  {/* Show the modal for accepting user input of required fields if an action is in
      progress, the preflight has been sent, fields are required, and fields are not
      yet submitted */}
  <Modal isOpen={!!backendActionInProgress && !!preflightResponse?.required.fields.length && !submittedFields} onClose={() => {}} id="truterritory-selection-action-input-modal">

    <Formik
      initialValues={preflightResponse?.required.fields.reduce((accum, field) => ({...accum, [field.column]: ""}), {}) || {}}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        setSubmittedFields(values);
      }}
    >
      {({ values, handleChange, handleSubmit, isSubmitting, errors, touched }) => (
        <>
        <form className="form-horizontal" onSubmit={handleSubmit}>
          <div className="modal-body col-md-12">
            <h4>Input Required</h4>
            <p>This action requires additional input.</p>

            { preflightResponse?.required.fields.map(field => (
            <div className="form-group" key={field.column}>
              <label className="control-label col-md-3" htmlFor={`action-required-${field.column}`}>
                {field.name}
              </label>
              <div className="col-md-6">
                { field.type == 'text' &&
                <Field name={field.column} type="text" id={`action-required-${field.column}`} className="form-control" />
                || 
                <Field
                  name={field.column}
                  type="number"
                  // inputmode="numeric"
                  pattern={field.type == "decimal" ? "\d+\.\d+" : "\d+"}
                  step={field.type == "decimal" ? 0.01 : 1}
                  id={`action-required-${field.column}`}
                  className="form-control"
                />
                }
              </div>
            </div>
            )) }
          </div>
          <div className="modal-footer">
            <div className="controls">
              {isSubmitting ? (
                <Button type="submit" variant="light-blue" disabled>
                  Running...
                </Button>
              ) : (
                <Button type="submit" variant="light-blue">
                  Continue
                </Button>
              )}
              <Button variant="plain" onClick={cancelAction}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
        </>
      )}
    </Formik>
  </Modal>

  {/* Show the modal for accepting user input of required decisions if an action is in
      in progress, the preflight has been sent, decisions are required, decisions are not
      yet submitted, fields have been submitted (so that this modal always shows second,
      not at the same time as fields) AND decisions have not been submitted (so that this
      isn't open at the same time as confirmation, which comes next). */}
  <Modal isOpen={!!backendActionInProgress && !!preflightResponse?.required.decisions.length && !!submittedFields && !confirmationMessages?.length && !backendActionInput} onClose={() => {}} id="truterritory-selection-action-input-modal">
    <Formik
      initialValues={preflightResponse?.required.decisions.reduce((accum, decision) => ({...accum, [decision.id]: ""}), {}) || {}}
      onSubmit={(values: Answers, { setSubmitting }) => {
        setSubmitting(true);
        setSubmittedDecisions(values);

        // If a chosen option requires confirmation, get its message.
        const decisions = preflightResponse?.required.decisions || [];
        const messages = Object.keys(values)
                               .map(decisionID => decisions.find(d => d.id == decisionID))
                               .map(decision => decision?.options.find(o => o.id == values[decision.id]))
                               .map(option => option?.confirmationMessage)
                               .filter(msg => !!msg);
        setConfirmationMessages(messages as string[]); // Yay for TS

        // If there are no messages, go ahead and mark this as confirmed.
        if (!messages.length) {
          setConfirmed(true);
        }
      }}
    >
      {({ values, handleChange, handleSubmit, isSubmitting, errors, touched }) => (
        <>
        <form className="form-horizontal" onSubmit={handleSubmit}>
          <div className="modal-body col-md-12">
            <h4>Decision Required</h4>

            { preflightResponse?.required.decisions.map((decision, index) => (
            <div key={index}>
              <p>{decision.description}</p>
              {decision.useImages && (
              <div className="image-buttons" >
                { decision.options.map(option => (
                <label key={option.id}>
                  <img src={optionImages[`${decision.id}.${option.id}`]} />
                  <Field name={`${decision.id}`} id={`decisions.${decision.id}.${option.id}`} type="radio" value={option.id} className="" />
                  <span>{ option.text }</span>
                </label>
                ))}
              </div>
              ) || (
              <div className="decision-options-radios">
                { decision.options.map(option => (
                <div key={option.id} className="decision-option">
                  <Field name={`${decision.id}`} id={`decisions.${decision.id}.${option.id}`} type="radio" value={option.id} className="" />
                  <label htmlFor={`decisions.${decision.id}.${option.id}`}>{ option.text }</label>
                </div>
                ))}
              </div>
              )}
            </div>
            ))}
          </div>
          <div className="modal-footer">
            <div className="controls">
              {isSubmitting ? (
                <Button type="submit" variant="light-blue" disabled>
                  Running...
                </Button>
              ) : (
                <Button type="submit" variant="light-blue">
                  Run
                </Button>
              )}
              <Button variant="plain" onClick={cancelAction}>
                Cancel
              </Button>
            </div>
          </div>
        </form>
        </>
      )}
    </Formik>
  </Modal>
  </>;
}